import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, FileText, Info } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { getAgentAnalysisReport } from '../../api/agentAnalysisReports';
import EmptyState from '../../components/ui/EmptyState';
import { getApiErrorMessage } from '../../utils/errors';
import ActionPlanCards from './ActionPlanCards';
import { hasCaseReferences, splitRecommendedActionsSection } from './utils';

const ReportMarkdown = React.lazy(() => import('./ReportMarkdown'));

function AgentAnalysisReportShow() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setReport(await getAgentAnalysisReport(reportId));
    } catch (err) {
      setReport(null);
      setError(getApiErrorMessage(err, 'Não foi possível carregar o relatório.'));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const actions = Array.isArray(report?.recommended_actions) ? report.recommended_actions : [];
  const markdownSections = splitRecommendedActionsSection(
    report?.output_markdown,
    actions.length > 0
  );
  const showCaseReferenceHelp = hasCaseReferences(report?.output_markdown);

  return (
    <div className="space-y-5">
      <Link
        to="/analysis-reports"
        className="inline-flex items-center gap-2 text-sm font-medium text-content-secondary hover:text-forge-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Voltar para análises
      </Link>

      {loading ? (
        <div className="surface-card px-4 py-12 text-center text-content-muted">
          Carregando relatório...
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4">
          <p className="text-sm text-red-700">{error}</p>
          <button type="button" onClick={loadReport} className="btn-secondary mt-3 text-sm px-3 py-2">
            Tentar novamente
          </button>
        </div>
      ) : report?.output_markdown ? (
        <div className="space-y-5">
          <React.Suspense
            fallback={
              <div className="surface-card px-5 py-12 text-center text-content-muted">
                Formatando relatório...
              </div>
            }
          >
            {markdownSections.before && (
              <div className="surface-card p-5 sm:p-8">
                {showCaseReferenceHelp && (
                  <div className="mb-6 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <p>
                      Códigos como <strong>C-a0f9</strong> são referências internas das conversas
                      analisadas. Eles servem apenas para rastrear a evidência e não são ações que
                      você precisa executar.
                    </p>
                  </div>
                )}
                <ReportMarkdown>{markdownSections.before}</ReportMarkdown>
              </div>
            )}

            <ActionPlanCards actions={actions} />

            {markdownSections.after && (
              <div className="surface-card p-5 sm:p-8">
                <ReportMarkdown>{markdownSections.after}</ReportMarkdown>
              </div>
            )}
          </React.Suspense>
        </div>
      ) : (
        <EmptyState
          icon={FileText}
          title="Relatório sem Markdown"
          description="A automação não enviou o campo output_markdown neste relatório."
        />
      )}
    </div>
  );
}

export default AgentAnalysisReportShow;
